import { useState } from "react";
import {
  BarChart3,
  ChevronDown,
  Coins,
  Eye,
  Gamepad2,
  Pencil,
  HelpCircle,
  MoreVertical,
  Plus,
  Search,
  Sparkles,
  Target,
  Ticket,
  Users,
} from "lucide-react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ChartExportButton, StartEndDates } from "../chart-controls";
import { defaultDateWindow } from "../chart-dates";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

type ExperienceType = "Spin" | "Scratch" | "Quiz";
type ExperienceStatus = "Live" | "Pending approval" | "Scheduled";

type ExperienceRow = {
  id: string;
  name: string;
  description: string;
  type: ExperienceType;
  status: ExperienceStatus;
  audience: string;
  participants: number;
  completion: number;
  pointsAwarded: string;
  ends: string;
};

const performanceData = [
  { month: "Jan", participants: 4200, points: 18000 },
  { month: "Feb", participants: 4800, points: 22000 },
  { month: "Mar", participants: 5600, points: 28000 },
  { month: "Apr", participants: 6400, points: 34000 },
  { month: "May", participants: 7600, points: 41000 },
  { month: "Jun", participants: 8640, points: 48600 },
];

const engagementByExperience = [
  {
    name: "Spin & Win",
    participants: 3420,
    percent: 39.6,
    width: "100%",
    barClass: "bg-green-600",
  },
  {
    name: "Scratch & Win",
    participants: 2860,
    percent: 33.1,
    width: "84%",
    barClass: "bg-green-500",
  },
  {
    name: "Quiz Challenge",
    participants: 1740,
    percent: 20.1,
    width: "51%",
    barClass: "bg-lime-500",
  },
  {
    name: "Birthday Spin",
    participants: 620,
    percent: 7.2,
    width: "18%",
    barClass: "bg-emerald-400",
  },
];

const experiences: ExperienceRow[] = [
  {
    id: "1",
    name: "Weekend Spin & Win",
    description: "Extra points on weekends",
    type: "Spin",
    status: "Live",
    audience: "All active members",
    participants: 3420,
    completion: 68,
    pointsAwarded: "18.4K pts",
    ends: "Oct 11",
  },
  {
    id: "2",
    name: "Festival Scratch & Win",
    description: "Seasonal scratch rewards",
    type: "Scratch",
    status: "Live",
    audience: "All members",
    participants: 2860,
    completion: 54,
    pointsAwarded: "12.8K pts",
    ends: "Oct 18",
  },
  {
    id: "3",
    name: "Gold Tier Quiz",
    description: "Quiz for Gold members",
    type: "Quiz",
    status: "Pending approval",
    audience: "Gold & above",
    participants: 1740,
    completion: 42,
    pointsAwarded: "9.2K pts",
    ends: "Oct 25",
  },
  {
    id: "4",
    name: "Birthday Spin",
    description: "Celebrate member birthdays",
    type: "Spin",
    status: "Scheduled",
    audience: "New members",
    participants: 620,
    completion: 28,
    pointsAwarded: "3.1K pts",
    ends: "Nov 02",
  },
];

const topExperiences = [
  {
    rank: 1,
    name: "Weekend Spin & Win",
    participants: "3,420",
    completion: "68%",
    points: "18.4K",
    progress: "92%",
  },
  {
    rank: 2,
    name: "Festival Scratch & Win",
    participants: "2,860",
    completion: "54%",
    points: "12.8K",
    progress: "78%",
  },
  {
    rank: 3,
    name: "Gold Tier Quiz",
    participants: "1,740",
    completion: "42%",
    points: "9.2K",
    progress: "61%",
  },
];

function MiniSparkline() {
  return (
    <svg viewBox="0 0 80 28" className="h-7 w-20 text-green-500" aria-hidden>
      <polyline
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        points="0,22 12,18 24,20 36,12 48,14 60,8 80,4"
      />
    </svg>
  );
}

function TypeBadge({ type }: { type: ExperienceType }) {
  const Icon = type === "Spin" ? Target : type === "Scratch" ? Ticket : HelpCircle;
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-sm font-medium text-slate-700">
      <Icon className="h-3.5 w-3.5" />
      {type}
    </span>
  );
}

function StatusBadge({ status }: { status: ExperienceStatus }) {
  const styles =
    status === "Live"
      ? "text-green-700"
      : status === "Pending approval"
        ? "text-orange-700"
        : "text-sky-700";
  const dot =
    status === "Live"
      ? "bg-green-500"
      : status === "Pending approval"
        ? "bg-orange-400"
        : "bg-sky-500";

  return (
    <span className={cn("inline-flex items-center gap-1.5 text-sm", styles)}>
      <span className={cn("h-2 w-2 rounded-full", dot)} />
      {status}
    </span>
  );
}

export default function RewardsGamification() {
  const [search, setSearch] = useState("");
  const initialRange = defaultDateWindow(30);
  const [from, setFrom] = useState(initialRange.from);
  const [to, setTo] = useState(initialRange.to);

  const filteredExperiences = experiences.filter((item) => {
    const query = search.trim().toLowerCase();
    if (!query) return true;
    return (
      item.name.toLowerCase().includes(query) ||
      item.description.toLowerCase().includes(query)
    );
  });

  return (
    <div className="space-y-6 pb-2">
      {/* Title */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
            Gamification
          </h1>
          <p className="mt-1 text-muted-foreground">
            Create engaging experiences that drive loyalty and repeat visits.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <StartEndDates from={from} to={to} onFrom={setFrom} onTo={setTo} />
          <Button type="button" variant="outline" className="bg-white">
            <Sparkles className="mr-1.5 h-4 w-4" />
            Create with AI
          </Button>
          <Button
            type="button"
            className="bg-green-600 text-white hover:bg-green-700"
          >
            <Plus className="mr-1.5 h-4 w-4" />
            Create experience
            <ChevronDown className="ml-1.5 h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm xl:flex-row xl:items-center">
        <div className="relative min-w-0 flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search experiences"
            className="h-10 pl-9"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <Select>
            <SelectTrigger className="h-10 w-[130px] bg-white">
              <SelectValue placeholder="All types" />
            </SelectTrigger>
            <SelectContent />
          </Select>
          <Select>
            <SelectTrigger className="h-10 w-[140px] bg-white">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent />
          </Select>
          <Select>
            <SelectTrigger className="h-10 w-[150px] bg-white">
              <SelectValue placeholder="All audiences" />
            </SelectTrigger>
            <SelectContent />
          </Select>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-green-50 text-green-700">
                <Gamepad2 className="h-5 w-5" />
              </div>
              <div className="flex flex-col items-end">
                <MiniSparkline />
                <p className="mt-1 text-sm font-medium text-green-700">
                  +2 this month
                </p>
              </div>
            </div>
            <p className="mt-4 text-sm text-muted-foreground">
              Active experiences
            </p>
            <p className="mt-1 text-3xl font-bold text-slate-900">6</p>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-green-50 text-green-700">
                <Users className="h-5 w-5" />
              </div>
              <div className="flex flex-col items-end">
                <MiniSparkline />
                <p className="mt-1 text-sm font-medium text-green-700">
                  +12.4%
                </p>
              </div>
            </div>
            <p className="mt-4 text-sm text-muted-foreground">Participants</p>
            <p className="mt-1 text-3xl font-bold text-slate-900">8,640</p>
            <p className="mt-1 text-sm text-muted-foreground">Members</p>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-green-50 text-green-700">
                <Coins className="h-5 w-5" />
              </div>
              <div className="flex flex-col items-end">
                <MiniSparkline />
                <p className="mt-1 text-sm font-medium text-green-700">+8.7%</p>
              </div>
            </div>
            <p className="mt-4 text-sm text-muted-foreground">Points awarded</p>
            <p className="mt-1 text-3xl font-bold text-slate-900">48.6K</p>
            <p className="mt-1 text-sm text-muted-foreground">Points</p>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-green-50 text-green-700">
                <BarChart3 className="h-5 w-5" />
              </div>
              <div className="flex flex-col items-end">
                <MiniSparkline />
                <p className="mt-1 text-sm font-medium text-green-700">+3.2%</p>
              </div>
            </div>
            <p className="mt-4 text-sm text-muted-foreground">Engagement rate</p>
            <p className="mt-1 text-3xl font-bold text-slate-900">24.8%</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid gap-4 xl:grid-cols-5">
        <Card className="border-slate-200 shadow-sm xl:col-span-3">
          <CardContent className="p-5">
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">
                  Gamification performance
                </h2>
                <p className="mt-0.5 text-sm text-muted-foreground">
                  Participation and points awarded over time
                </p>
              </div>
              <ChartExportButton
                filename="gamification-performance.csv"
                rows={performanceData.map((row) => ({
                  Month: row.month,
                  Participants: row.participants,
                  "Points awarded": row.points,
                }))}
              />
            </div>
            <div className="h-[280px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={performanceData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis
                    dataKey="month"
                    tick={{ fill: "#64748b", fontSize: 12 }}
                  />
                  <YAxis tick={{ fill: "#64748b", fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="participants"
                    name="Participants"
                    stroke="#15803d"
                    strokeWidth={2.5}
                    dot={{ r: 3 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="points"
                    name="Points awarded"
                    stroke="#86efac"
                    strokeWidth={2.5}
                    dot={{ r: 3 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm xl:col-span-2">
          <CardContent className="flex h-full flex-col p-5">
            <div className="mb-5 flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">
                  Engagement by experience
                </h2>
                <p className="mt-0.5 text-sm text-muted-foreground">
                  Participation across active experiences
                </p>
              </div>
              <ChartExportButton
                filename="gamification-engagement.csv"
                rows={engagementByExperience.map((item) => ({
                  Experience: item.name,
                  Participants: item.participants,
                  Share: item.percent,
                }))}
              />
            </div>
            <div className="space-y-4">
              {engagementByExperience.map((item) => (
                <div key={item.name}>
                  <div className="mb-1.5 flex items-center justify-between gap-3 text-sm">
                    <span className="font-medium text-slate-700">
                      {item.name}
                    </span>
                    <span className="text-slate-700">
                      <span className="font-semibold text-slate-900">
                        {item.participants.toLocaleString("en-IN")}
                      </span>{" "}
                      <span className="text-muted-foreground">
                        ({item.percent}%)
                      </span>
                    </span>
                  </div>
                  <div className="h-2.5 overflow-hidden rounded-full bg-slate-100">
                    <div
                      className={cn("h-full rounded-full", item.barClass)}
                      style={{ width: item.width }}
                    />
                  </div>
                </div>
              ))}
            </div>
            <button
              type="button"
              className="mt-auto pt-6 text-left text-sm font-semibold text-green-700"
            >
              View all experiences →
            </button>
          </CardContent>
        </Card>
      </div>

      {/* Experiences table */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-5 sm:p-6">
          <div className="mb-4 flex items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">
                Gamification experiences
              </h2>
              <p className="mt-0.5 text-sm text-muted-foreground">
                Active and recently completed loyalty experiences.
              </p>
            </div>
            <button
              type="button"
              className="shrink-0 text-sm font-semibold text-green-700"
            >
              View all →
            </button>
          </div>

          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/80 hover:bg-slate-50/80">
                  <TableHead>Experience</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Audience</TableHead>
                  <TableHead>Participants</TableHead>
                  <TableHead>Completion</TableHead>
                  <TableHead>Points awarded</TableHead>
                  <TableHead>Ends</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredExperiences.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={9}
                      className="py-10 text-center text-sm text-muted-foreground"
                    >
                      No experiences match your search.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredExperiences.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <div>
                          <p className="font-semibold text-slate-900">
                            {item.name}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {item.description}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <TypeBadge type={item.type} />
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={item.status} />
                      </TableCell>
                      <TableCell className="text-slate-700">
                        {item.audience}
                      </TableCell>
                      <TableCell className="font-medium text-slate-800">
                        {item.participants.toLocaleString("en-IN")}
                      </TableCell>
                      <TableCell>
                        <div className="min-w-[100px] space-y-1.5">
                          <p className="text-sm font-medium text-slate-800">
                            {item.completion}%
                          </p>
                          <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
                            <div
                              className="h-full rounded-full bg-green-600"
                              style={{ width: `${item.completion}%` }}
                            />
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-700">
                        {item.pointsAwarded}
                      </TableCell>
                      <TableCell className="text-slate-600">
                        {item.ends}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                            >
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem>
                              <Eye className="mr-2 h-4 w-4" />
                              View details
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <Pencil className="mr-2 h-4 w-4" />
                              Edit
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Top performing */}
      <section className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">
            Top performing experiences
          </h2>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Experiences generating the strongest member engagement.
          </p>
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          {topExperiences.map((item) => (
            <Card key={item.rank} className="border-slate-200 shadow-sm">
              <CardContent className="p-5">
                <div className="mb-4 flex items-center gap-3">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-green-50 text-sm font-semibold text-green-700">
                    {item.rank}
                  </span>
                  <h3 className="font-semibold text-slate-900">{item.name}</h3>
                </div>
                <div className="mb-4 h-2 overflow-hidden rounded-full bg-slate-100">
                  <div
                    className="h-full rounded-full bg-green-600"
                    style={{ width: item.progress }}
                  />
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div>
                    <p className="text-xs text-muted-foreground">Participants</p>
                    <p className="mt-1 text-sm font-semibold text-slate-900">
                      {item.participants}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Completion</p>
                    <p className="mt-1 text-sm font-semibold text-slate-900">
                      {item.completion}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Points</p>
                    <p className="mt-1 text-sm font-semibold text-slate-900">
                      {item.points}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>
    </div>
  );
}
